/** Client mirror of the exam gate in functions/src/scoring.ts.
 *  An exam is a quiz whose masteryMinCorrect is an integer >= 1 (the same field
 *  the catalog API and a direct step read both expose). It stays locked while any
 *  other step with a numeric order lower than the exam's is not done === true.
 *  The server also ignores a bar above the question count; the client does not
 *  have that count on the track list, so a published bar is enough to warn. */

export interface ExamLockStep {
  id: string;
  order: number;
  type?: string;
  masteryMinCorrect?: number;
}

export interface ExamLockProgress {
  steps?: Record<string, { done?: boolean } | undefined>;
}

export interface ExamLockState {
  isExam: boolean;
  locked: boolean;
  earlierDone: number;
  earlierTotal: number;
}

const OPEN: ExamLockState = { isExam: false, locked: false, earlierDone: 0, earlierTotal: 0 };

function isMasteryExam(step: ExamLockStep): boolean {
  if (step.type !== 'quiz') return false;
  const bar = step.masteryMinCorrect;
  return typeof bar === 'number' && Number.isInteger(bar) && bar >= 1;
}

export function examLockState(
  steps: readonly ExamLockStep[],
  progress: ExamLockProgress | null | undefined,
  examStepId: string,
): ExamLockState {
  const exam = steps.find((s) => s.id === examStepId);
  if (!exam || !isMasteryExam(exam)) return OPEN;
  // scoring.ts skips the gate when the exam's own order is not a number.
  if (typeof exam.order !== 'number') {
    return { isExam: true, locked: false, earlierDone: 0, earlierTotal: 0 };
  }

  let earlierDone = 0;
  let earlierTotal = 0;
  for (const step of steps) {
    if (step.id === examStepId) continue;
    if (typeof step.order !== 'number' || !(step.order < exam.order)) continue;
    earlierTotal += 1;
    if (progress?.steps?.[step.id]?.done === true) earlierDone += 1;
  }
  return {
    isExam: true,
    locked: earlierDone < earlierTotal,
    earlierDone,
    earlierTotal,
  };
}

/** Banner sentence, or null when the exam is open. */
export function examLockMessage(state: ExamLockState): string | null {
  if (!state.locked) return null;
  return `Finish the earlier steps first — ${state.earlierDone} of ${state.earlierTotal} done.`;
}
